import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { completeGoogleSignIn, isFirebaseConfigured, startGoogleSignIn, tokenFor } from "./firebase";
import { FREE_TEST_MAC_DOWNLOAD_URL, macDownloadUrl } from "./platform";
import { LegalLinks, LegalPage } from "./legal";
import "./styles.css";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");
const nativeMacDownloadUrl = macDownloadUrl(import.meta.env.VITE_MAC_DOWNLOAD_URL);

function App() {
  return <main>
    <header className="titlebar"><span className="brand-asset" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span><span>Orécce</span><nav><a href="#how">Setup</a><a href="#pricing">Pricing</a><a href="/free">Free</a><a href="#download">Download</a></nav></header>
    <section className="hero" id="download">
      <div className="hero-mark" aria-hidden="true"><span className="brand-asset large"><img src="/orecce-mark.svg" alt="" /></span></div>
      <h1>Get extra juice from your codex subscription.</h1>
      <p className="lede">Continue using ChatGPT for your codebase even after hitting your codex limits.</p>
      <div className="actions"><a className="button primary" href={nativeMacDownloadUrl}>Download for Mac</a></div>
    </section>
    <section id="how" className="window">
      <div className="window-title"><span>◈</span> ORÉCCE SETUP</div>
      <ol>
        <li><span className="step">1</span><b>Sign in with Google</b></li>
        <li><span className="step">2</span><b>Select your GitHub projects</b></li>
      </ol>
    </section>
    <section className="window usage-card">
      <div className="window-title"><span>◈</span> ORÉCCE USAGE</div>
      <p className="usage-subtitle">When codex goes down,</p>
      <ol>
        <li><span className="step">1</span><b>Open Orécce</b></li>
        <li><span className="step">2</span><b>Copy the given message to ChatGPT</b></li>
        <li><span className="step">3</span><b>Start working</b></li>
      </ol>
    </section>
    <section id="pricing" className="pricing">
      <article><p className="plan-tag">NORMAL</p><h2>10 repos</h2><p className="price">$5</p></article>
      <article className="featured"><p className="plan-tag">PRO</p><h2>Unlimited repos</h2><p className="price">$15</p></article>
    </section>
    <div className="pricing-download"><a className="button primary" href={nativeMacDownloadUrl}>Download for Mac</a></div>
    <footer><span className="brand-asset tiny" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span> ORÉCCE · MAC <LegalLinks /></footer>
  </main>;
}

function FreeDownload() {
  return <main>
    <header className="titlebar"><a className="legal-brand" href="/"><span className="brand-asset" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span><span>Orécce</span></a><nav><a href="/">Home</a></nav></header>
    <section className="hero free-hero" id="download">
      <div className="hero-mark" aria-hidden="true"><span className="brand-asset large"><img src="/orecce-mark.svg" alt="" /></span></div>
      <p className="free-kicker">ONE PROJECT · FREE TEST BUILD</p>
      <h1>Use Orécce free.</h1>
      <p className="lede">Download the Mac app, sign in with Google, and prepare one GitHub project for ChatGPT at no cost.</p>
      <div className="actions"><a className="button primary" href={FREE_TEST_MAC_DOWNLOAD_URL}>Download free test for Mac</a></div>
      <p className="download-note">macOS 13 or later · Apple silicon · notarized · no purchase required</p>
    </section>
    <section className="window free-steps">
      <div className="window-title"><span>◈</span> FREE SETUP</div>
      <ol>
        <li><span className="step">1</span><b>Install Orécce for Mac</b></li>
        <li><span className="step">2</span><b>Sign in with Google</b></li>
        <li><span className="step">3</span><b>Connect Drive and GitHub</b></li>
        <li><span className="step">4</span><b>Choose one project</b></li>
      </ol>
    </section>
    <footer><span className="brand-asset tiny" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span> ORÉCCE FREE · MAC <LegalLinks /></footer>
  </main>;
}

/** Keeps the wording plain while still naming the actual cause. */
function signInFailureMessage(error: unknown): string {
  const code = typeof error === "object" && error && "code" in error ? String((error as { code: unknown }).code) : String(error);
  if (code.includes("unauthorized-domain")) return "This site is not approved for Google sign-in yet.";
  if (code.includes("popup") || code.includes("cancelled") || code.includes("canceled")) return "Google sign-in was closed before it finished. Please try again.";
  if (code.includes("network")) return "Google could not be reached. Check your connection and try again.";
  if (code.includes("pairing_failed")) return "Signed in, but Orécce could not be connected. Start again from the app.";
  return "Google sign-in did not complete. Please try again.";
}

function DesktopLogin() {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams(window.location.search);

  // Google sends the browser back to this same URL, so the request and claim
  // are still in the query string when the redirect result is read.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const user = await completeGoogleSignIn();
        if (!user || cancelled) return;
        setBusy(true);
        await pairDesktop(user);
      } catch (error) {
        if (!cancelled) setMessage(signInFailureMessage(error));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function pairDesktop(user: Parameters<typeof tokenFor>[0]) {
    const requestId = params.get("request");
    const claimSecret = params.get("claim");
    if (!requestId || !claimSecret) { setMessage("This sign-in link has expired. Start again from Orécce."); return; }
    const response = await fetch(`${apiBaseUrl}/v1/desktop-auth/complete`, {
      method: "POST",
      headers: { authorization: `Bearer ${await tokenFor(user)}`, "content-type": "application/json" },
      body: JSON.stringify({ requestId, claimSecret })
    });
    if (!response.ok) throw new Error(`pairing_failed_${response.status}`);
    setMessage("You can return to Orécce.");
  }

  async function complete() {
    if (!params.get("request") || !params.get("claim")) { setMessage("This sign-in link has expired. Start again from Orécce."); return; }
    setBusy(true);
    setMessage("");
    try {
      await startGoogleSignIn();
    } catch (error) {
      setMessage(signInFailureMessage(error));
      setBusy(false);
    }
  }

  return <main className="desktop-login"><section className="login-window"><span className="brand-asset large" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span><h1>Build cooler things?</h1><button className="primary" disabled={busy || !isFirebaseConfigured()} onClick={() => void complete()}>{busy ? "Opening Google…" : "Continue with Google"}</button>{message && <p className="status" role="status">{message}</p>}<p>Orécce never asks for your Google password.</p></section></main>;
}

function ConnectionComplete() {
  return <main className="desktop-login"><section className="login-window"><span className="brand-asset large" aria-hidden="true"><img src="/orecce-mark.svg" alt="" /></span><h1>Connected.</h1><p>You can return to Orécce.</p></section></main>;
}

const legalPath = window.location.pathname === "/privacy" ? "privacy" : window.location.pathname === "/terms" ? "terms" : window.location.pathname === "/refunds" ? "refunds" : null;
createRoot(document.getElementById("root")!).render(legalPath ? <LegalPage kind={legalPath} /> : window.location.pathname === "/desktop-login" ? <DesktopLogin /> : window.location.pathname === "/desktop-connected" ? <ConnectionComplete /> : window.location.pathname === "/free" ? <FreeDownload /> : <App />);
